import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { CustomButton } from '../../../../common/components';
import LotPlanCanvas from './LotPlanCanvas';
import LotDialog from './LotDialog';
import { useLotPlan } from '../hooks/useLotPlan';
import { useMemo, useState } from 'react';

const LotsPlanView = ({ allotment, canEditLots = true }) => {
  const [dragEnabled, setDragEnabled] = useState(false);
  const [referencesOpen, setReferencesOpen] = useState(false);
  const {
    lots,
    allLots,
    isLoading,
    projects,
    contracts,
    constructionTypes,
    selectedLot,
    detailsOpen,
    createOpen,
    draftLot,
    handleOpenCreate,
    handleCloseCreate,
    handleCreateLot,
    handleSelectLot,
    handleCloseDetails,
    handleUpdateLot,
    handleDragEnd,
    selectedProject,
    selectedContract,
    subscribers,
  } = useLotPlan(allotment?.uuid);

  const projectsMap = useMemo(() => {
    return projects.reduce((acc, project) => {
      if (project.uuid_lot) {
        acc[project.uuid_lot] = project;
      }
      return acc;
    }, {});
  }, [projects]);

  if (!allotment?.uuid) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Seleccioná un loteo para visualizar el plano.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          p: 2,
          borderBottom: '3px solid #e0e0e0',
          borderTop: '3px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Plano | {allotment.name}
          </Typography>
          <CustomButton variant="outlined" onClick={() => setReferencesOpen(true)}>
            Referencias
          </CustomButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={dragEnabled}
                onChange={(event) => setDragEnabled(event.target.checked)}
                disabled={!canEditLots}
              />
            }
            label="Mover lotes"
          />
          <CustomButton onClick={handleOpenCreate} variant="base" disabled={!canEditLots}>
            Agregar lote
          </CustomButton>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <LotPlanCanvas
          allotment={allotment}
          lots={lots}
          projectsMap={projectsMap}
          constructionTypes={constructionTypes}
          onLotClick={handleSelectLot}
          onLotDragEnd={handleDragEnd}
          isLoading={isLoading}
          dragEnabled={dragEnabled && canEditLots}
        />
      </Box>

      <LotDialog
        open={createOpen}
        onClose={handleCloseCreate}
        editMode={false}
        selectedLot={draftLot}
        onSave={handleCreateLot}
        canEditLots={canEditLots}
        contracts={contracts}
        constructionTypes={constructionTypes}
        allLots={allLots}
      />

      <LotDialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        editMode={true}
        selectedLot={selectedLot}
        contracts={contracts}
        constructionTypes={constructionTypes}
        allLots={allLots}
        project={selectedProject}
        contract={selectedContract}
        subscribers={subscribers}
        onSave={async (lot) => {
          await handleUpdateLot(lot);
          handleCloseDetails();
        }}
        canEditLots={canEditLots}
      />

      <Dialog
        open={referencesOpen}
        onClose={() => setReferencesOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Referencias</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {[
              {
                label: 'Constructora Colonial - Obra en progreso',
                background: '#ff9800',
                color: '#111111',
                border: '#da8300',
                spinner: true,
                spinnerColor: '#ffb74d',
              },
              {
                label: 'No disponible',
                background: '#9e9e9e',
                color: '#ffffff',
                border: '#858585',
              },
              {
                label: 'Obra completada',
                background: '#d32f2f',
                color: '#ffffff',
                border: '#b92a2a',
              },
              {
                label: 'Asignado a Von Der Heyde',
                background: '#1b1b1b',
                color: '#ffffff',
                border: '#4d4d4d',
              },
              {
                label: 'Asignado a Constructora Colonial',
                background: '#ff9800',
                color: '#111111',
                border: '#da8300',
              },
              {
                label: 'Disponible',
                background: '#2e7d32',
                color: '#ffffff',
                border: '#246727',
              },
              {
                label: 'Boleto asociado',
                background: '#81d4fa',
                color: '#111111',
                border: '#4fc3f7',
              },
              {
                label: 'Asignado a un contrato',
                background: '#1565c0',
                color: '#ffffff',
                border: '#135bae',
              },
              {
                label: 'Obra en proceso',
                background: '#1565c0',
                color: '#ffffff',
                border: '#135bae',
                spinner: true,
              },
            ].map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: item.background,
                    color: item.color,
                    borderStyle: 'solid',
                    borderWidth: 4,
                    borderColor: item.border,
                    borderTopColor: item.spinner ? item.spinnerColor || item.border : item.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    position: 'relative',
                    '&::before': item.spinner
                      ? {
                          content: '""',
                          position: 'absolute',
                          inset: -4,
                          borderRadius: '50%',
                          border: `4px solid ${item.border}`,
                          borderTopColor: item.spinnerColor || item.border,
                          '@keyframes lotLegendSpin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                          },
                          animation: 'lotLegendSpin 1s linear infinite',
                        }
                      : undefined,
                  }}
                >
                  1
                </Box>
                <Typography variant="body2">{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LotsPlanView;
